import { useState, useEffect, useRef } from 'react';
import { ScrollText, Server, Shield, AlertTriangle, RefreshCw, Loader2, Filter, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { adminToolsService } from '../../services';
import { toast } from 'sonner';

const LogLine = ({ line, index }) => {
  const isError = /error|exception|traceback|failed/i.test(line);
  const isWarning = /warning|warn/i.test(line);
  return (
    <div
      className="font-mono text-xs px-3 py-0.5 border-b"
      style={{
        borderColor: 'var(--color-border)',
        color: isError ? 'var(--color-error)' : isWarning ? 'var(--color-warning)' : 'var(--color-text-primary)',
        backgroundColor: isError ? 'rgba(239,68,68,0.05)' : isWarning ? 'rgba(255,152,0,0.05)' : 'transparent'
      }}
    >
      <span className="opacity-40 mr-2 select-none">{index + 1}</span>
      {line}
    </div>
  );
};

const ServerLogsTab = () => {
  const [logs, setLogs] = useState({ lines: [], total_lines: 0 });
  const [logType, setLogType] = useState('backend_err');
  const [loading, setLoading] = useState(true);
  const logEndRef = useRef(null);

  const loadLogs = async (type) => {
    setLoading(true);
    try {
      const data = await adminToolsService.getServerLogs(type, 500);
      setLogs(data);
      setTimeout(() => logEndRef.current?.scrollIntoView(), 100);
    } catch (e) {
      toast.error('Fehler beim Laden der Logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(logType); }, [logType]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Select value={logType} onValueChange={v => setLogType(v)}>
            <SelectTrigger className="w-48 h-8 text-sm" data-testid="server-log-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="backend_err">Backend Error Log</SelectItem>
              <SelectItem value="backend_out">Backend Output Log</SelectItem>
              <SelectItem value="frontend_err">Frontend Error Log</SelectItem>
              <SelectItem value="frontend_out">Frontend Output Log</SelectItem>
            </SelectContent>
          </Select>
          <Badge variant="outline">{logs.total_lines} Zeilen</Badge>
        </div>
        <Button size="sm" variant="outline" onClick={() => loadLogs(logType)} disabled={loading}>
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </Button>
      </div>
      <Card style={{ borderColor: 'var(--color-border)' }}>
        <CardContent className="p-0 max-h-[500px] overflow-auto" style={{ backgroundColor: 'var(--color-background)' }}>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-primary)' }} /></div>
          ) : logs.lines.length === 0 ? (
            <p className="text-center py-8 text-sm" style={{ color: 'var(--color-text-secondary)' }}>Keine Logs vorhanden</p>
          ) : (
            <>
              {logs.lines.map((line, i) => <LogLine key={i} line={line} index={i} />)}
              <div ref={logEndRef} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const AuditLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);

  const loadLogs = async (s = 0) => {
    setLoading(true);
    try {
      const data = await adminToolsService.getAuditLogs({ skip: s, limit: 50, days: 90 });
      setLogs(data.logs || []);
      setTotal(data.total);
      setSkip(s);
    } catch (e) {
      toast.error('Fehler beim Laden der Audit-Logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(); }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Badge variant="outline">{total} Einträge (letzte 90 Tage)</Badge>
        <Button size="sm" variant="outline" onClick={() => loadLogs(0)} disabled={loading}>
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </Button>
      </div>
      <Card style={{ borderColor: 'var(--color-border)' }}>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-primary)' }} /></div>
          ) : logs.length === 0 ? (
            <p className="text-center py-8 text-sm" style={{ color: 'var(--color-text-secondary)' }}>Keine Audit-Logs vorhanden</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: 'var(--color-background)' }} className="border-b">
                  <th className="text-left p-3 font-medium" style={{ color: 'var(--color-text-primary)' }}>Zeitpunkt</th>
                  <th className="text-left p-3 font-medium" style={{ color: 'var(--color-text-primary)' }}>Benutzer</th>
                  <th className="text-left p-3 font-medium" style={{ color: 'var(--color-text-primary)' }}>Aktion</th>
                  <th className="text-left p-3 font-medium" style={{ color: 'var(--color-text-primary)' }}>Kategorie</th>
                  <th className="text-left p-3 font-medium" style={{ color: 'var(--color-text-primary)' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => {
                  // Handle nested actor object
                  const actorEmail = log.actor?.email || log.actor_email || log.user_id || '-';
                  // Handle nested action object
                  const actionText = typeof log.action === 'object' 
                    ? (log.action?.action_type || log.action?.description || '-')
                    : (log.action || log.action_type || '-');
                  // Handle category - might be in action object or root
                  const category = typeof log.action === 'object'
                    ? (log.action?.category || log.category || '-')
                    : (log.category || '-');
                  
                  return (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-mono text-xs whitespace-nowrap">{log.timestamp ? new Date(log.timestamp).toLocaleString('de-DE') : '-'}</td>
                      <td className="p-3">{actorEmail}</td>
                      <td className="p-3"><Badge variant="outline" className="text-xs">{actionText}</Badge></td>
                      <td className="p-3"><Badge className="text-xs">{category}</Badge></td>
                      <td className="p-3 text-xs max-w-[200px] truncate" style={{ color: 'var(--color-text-secondary)' }}>
                        {log.details ? (typeof log.details === 'string' ? log.details : JSON.stringify(log.details)) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      {total > 50 && (
        <div className="flex justify-between items-center">
          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{skip + 1}–{Math.min(skip + 50, total)} von {total}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => loadLogs(Math.max(0, skip - 50))}>Zurück</Button>
            <Button size="sm" variant="outline" disabled={skip + 50 >= total} onClick={() => loadLogs(skip + 50)}>Weiter</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const LogsPanel = () => {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <ScrollText className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
        <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>System Logs</h3>
      </div>
      <Tabs defaultValue="server" className="space-y-4">
        <TabsList style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)' }} className="border p-1">
          <TabsTrigger value="server" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="logs-server-tab">
            <Server className="w-3 h-3 mr-1" /> Server Logs
          </TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="logs-audit-tab">
            <Shield className="w-3 h-3 mr-1" /> Audit Logs
          </TabsTrigger>
          <TabsTrigger value="errors" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="logs-errors-tab">
            <AlertTriangle className="w-3 h-3 mr-1" /> Error Logs
          </TabsTrigger>
        </TabsList>
        <TabsContent value="server"><ServerLogsTab /></TabsContent>
        <TabsContent value="audit"><AuditLogsTab /></TabsContent>
        <TabsContent value="errors"><ServerLogsTab /></TabsContent>
      </Tabs>
    </div>
  );
};
